import { useEffect, useRef } from 'react';

const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled]):not([type="hidden"])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(', ');

function focusableElements(root: HTMLElement): HTMLElement[] {
    return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (element) => !element.hasAttribute('disabled') && element.tabIndex >= 0,
    );
}

/**
 * Modal shell behavior: remember the trigger, move focus into the dialog, close on Escape
 * or a click on the dialog layer itself, keep Tab inside, and restore the trigger on close.
 */
function useDismissibleDialog(onClose: () => void) {
    const dialogRef = useRef<HTMLDivElement>(null);
    const onCloseRef = useRef(onClose);
    const triggerRef = useRef<HTMLElement | null>(null);
    onCloseRef.current = onClose;

    useEffect(() => {
        const active = document.activeElement;
        triggerRef.current = active instanceof HTMLElement ? active : null;

        const node = dialogRef.current;
        if (node) {
            node.focus();
        }

        const restoreTrigger = () => {
            const trigger = triggerRef.current;
            const root = dialogRef.current;
            if (!trigger || !document.contains(trigger)) {
                return;
            }
            if (root && root.contains(trigger)) {
                return;
            }
            trigger.focus();
        };

        const requestClose = () => {
            onCloseRef.current();
            restoreTrigger();
        };

        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                if (event.defaultPrevented) {
                    return;
                }
                event.preventDefault();
                requestClose();
                return;
            }
            if (event.key !== 'Tab' || event.defaultPrevented || !node) {
                return;
            }

            const items = focusableElements(node);
            if (items.length === 0) {
                event.preventDefault();
                node.focus();
                return;
            }

            const first = items[0];
            const last = items[items.length - 1];
            const activeEl = document.activeElement;
            const inside = activeEl instanceof Node && node.contains(activeEl);
            if (!inside || activeEl === node) {
                event.preventDefault();
                (event.shiftKey ? last : first).focus();
                return;
            }
            if (event.shiftKey && activeEl === first) {
                event.preventDefault();
                last.focus();
                return;
            }
            if (!event.shiftKey && activeEl === last) {
                event.preventDefault();
                first.focus();
            }
        };

        const onClick = (event: MouseEvent) => {
            if (event.target === event.currentTarget) {
                requestClose();
            }
        };

        document.addEventListener('keydown', onKeyDown, true);
        node?.addEventListener('click', onClick);

        return () => {
            document.removeEventListener('keydown', onKeyDown, true);
            node?.removeEventListener('click', onClick);
            restoreTrigger();
        };
    }, []);

    return dialogRef;
}

export default useDismissibleDialog;
